/**
 * "Can we actually email you?" for the signed-in user.
 *
 * Reads GET /api/email/deliverability/me, which reports the current suppression state for the
 * address we would really send to.
 *
 * Why this exists: mail to Apple "Hide My Email" relay aliases was being rejected outright
 * (`550 5.1.1 ... unauthorized sender`) and the user was told nothing at all — billing notices
 * simply stopped arriving. The backend now records bounces; this is the half that says so.
 *
 * The `causedByOurConfiguration` distinction is the important one. When the block is ours, the
 * user's address is fine and prompting them to change it would be actively wrong, so the backend
 * ships the copy and the client renders it rather than inventing a second set of messages.
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface EmailDeliverability {
  email: string | null;
  deliverable: boolean;
  isAppleRelay: boolean;
  /** True when we are the problem. Never ask the user to change their address in this case. */
  causedByOurConfiguration: boolean;
  category: string | null;
  lastEventAt: string | null;
  /** Ready-to-render copy, or null when everything is fine. */
  message: string | null;
}

export const emailDeliverabilityQueryKey = ['email', 'deliverability', 'me'] as const;

export function useEmailDeliverability(enabled: boolean = true) {
  return useQuery<EmailDeliverability>({
    queryKey: emailDeliverabilityQueryKey,
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: EmailDeliverability }>(
        '/api/email/deliverability/me'
      );
      return response.data;
    },
    enabled,
    // Deliverability changes at the speed of a bounce webhook, not of a click. Long stale time,
    // no refetch on focus: this is a passive banner, not something worth a request per tab switch.
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    // A failure here must be invisible. Not knowing whether email works is not worth an error
    // state on top of every page.
    retry: false,
  });
}
